/**
 * §14.8 screen 8 — AI nazorat va tahlil.
 *
 * ===========================================================================
 * THE HARDEST THING THIS SCREEN DOES IS REFUSE TO TAKE CREDIT.
 * ===========================================================================
 *
 * Three of its four blocks contain no model output at all. Deadline risk is
 * `coalesce(extended_due_at, due_at)` arithmetic; anomalies are a mahalla's
 * weekly volume against a trailing mean and a channel that has gone quiet.
 * The reference product presents both as artificial intelligence. §14.4.5
 * rule 6 forbids that outright — "calling SQL 'AI' is the cheapest lie
 * available on this screen" — so each of those blocks carries
 * `admin.ai.sqlBasis` above it, always, not behind a tooltip and not once on
 * first view.
 *
 * The API sends `basis: 'sql'` on every one of those rows, so this is not a
 * component's good intentions: the contract carries the claim and this screen
 * renders what it was sent. `SQL_BASIS` below refuses to render an AI badge for
 * anything whose basis is `sql`, which makes the rule structural rather than
 * decorative.
 *
 * ===========================================================================
 * AND WHEN THERE IS NO PROVIDER, IT SAYS SO.
 * ===========================================================================
 *
 * `ANALYZE_PROVIDER=off` is the production state until the F-8/§10.1 legal
 * opinion exists (§14.10.9). An empty board on that deployment would read as
 * "nothing found", which §14.4.5 rule 5 names as the failure. So `provider.off`
 * renders an explicit sentence — the feature is switched off in this
 * environment — while the two SQL blocks keep working, because they never
 * needed a provider in the first place.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '@navoiy/api-types';
import { AI_TOPICS, SQL_RISK_MAX, SQL_RISK_WEIGHTS } from '@navoiy/domain';
import { ApiError, api, errorKey, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { BarChart, SERIES_COLORS, type Slice } from '../components/charts/index.jsx';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { formatDate } from '../lib/format.js';
import {
  Badge,
  Card,
  EmptyState,
  ErrorBanner,
  Spinner,
  Table,
  Td,
  Th,
  Tr,
  WarningBanner,
} from '../ui/index.jsx';

type Board = components['schemas']['AdminAiBoard'];
type RiskItem = components['schemas']['AiDeadlineRiskItem'];
type SqlRisk = components['schemas']['AiSqlRisk'];

/**
 * The four deadline buckets, coloured to match their urgency and NOT to match
 * the status badges — a `critical` appeal is `in_review` like a hundred others,
 * and colouring by status here would tell a reader nothing.
 */
const BUCKET_TONE: Record<string, 'danger' | 'gold' | 'brand' | 'neutral'> = {
  overdue: 'danger',
  critical: 'danger',
  warning: 'gold',
  ok: 'neutral',
};

export function AiScreen(): React.JSX.Element {
  const { t, locale } = useI18n();
  const { expire } = useSession();

  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = unwrap(await api().GET('/admin/ai/board', { params: { query: { locale } } }));
      setBoard(data);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        expire();
        return;
      }
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    } finally {
      setLoading(false);
    }
  }, [locale, expire, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !board) return <Spinner label={t('admin.common.loading')} />;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-brand-900">{t('admin.ai.title')}</h1>
        {/* §14.1 — an unlabelled scoped list is a lie. A handler must not read
            twenty rows as the city's twenty worst. */}
        {board?.scoped ? <Badge tone="brand">{t('admin.scoped.label')}</Badge> : null}
      </div>

      <ErrorBanner message={error} />

      {board ? (
        <>
          <ProviderCard provider={board.provider} />

          {/* ------------------------------------------------------------
              Block 1 — deadline risk. PURE SQL, and it says so first.
              ------------------------------------------------------------ */}
          <Card title={t('admin.ai.deadlineTitle')}>
            <div className="flex flex-col gap-4">
              <SqlBasisNote />
              {board.deadline_risk.length === 0 ? (
                <EmptyState message={t('admin.ai.deadlineEmpty')} />
              ) : (
                <Table
                  head={
                    <tr>
                      <Th>{t('admin.column.reference')}</Th>
                      <Th>{t('admin.column.mahalla')}</Th>
                      <Th>{t('admin.column.status')}</Th>
                      <Th>{t('admin.column.due')}</Th>
                      <Th>{t('admin.ai.riskTitle')}</Th>
                    </tr>
                  }
                >
                  {board.deadline_risk.map((item) => (
                    <RiskRow key={item.id} item={item} />
                  ))}
                </Table>
              )}
            </div>
          </Card>

          {/* ------------------------------------------------------------
              Block 2 — anomalies. ALSO pure SQL, also labelled.
              ------------------------------------------------------------ */}
          <Card title={t('admin.ai.anomalyTitle')}>
            <div className="flex flex-col gap-4">
              <SqlBasisNote />
              {board.anomalies.length === 0 ? (
                <EmptyState message={t('admin.ai.anomalyEmpty')} />
              ) : (
                <ul className="flex flex-col gap-3">
                  {board.anomalies.map((anomaly, index) => (
                    <li
                      key={`${anomaly.kind}-${anomaly.subject}-${String(index)}`}
                      className="flex flex-col gap-1 rounded-chip border-1 border-line-200 px-4 py-3"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone="gold">{t(`admin.ai.anomaly.${anomaly.kind}`)}</Badge>
                        <span className="text-sm font-semibold text-ink-900">
                          {/* A channel anomaly's `subject` is a raw channel
                              value and is rendered through the panel's own
                              dictionary; a mahalla's is already localized by
                              the server. Concatenating either into a sentence
                              server-side would be untranslatable. */}
                          {anomaly.kind === 'channel_silent'
                            ? t(`admin.channel.${anomaly.subject}`)
                            : anomaly.subject}
                        </span>
                      </div>
                      <p className="text-sm text-ink-700">
                        {t(`admin.ai.anomalyDetail.${anomaly.kind}`, {
                          observed: anomaly.observed,
                          expected: anomaly.expected,
                          sigma: anomaly.sigma ?? '—',
                        })}
                      </p>
                      {/* A silent channel and an unplugged kiosk are different
                          incidents with different owners. The panel must not
                          report the second as the first. */}
                      {anomaly.device_hint ? (
                        <p className="text-xs font-medium text-danger-600">
                          {t('admin.ai.anomalyDeviceHint')}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          {/* ------------------------------------------------------------
              Block 3 — topic trend. `mixed`: SQL counts, model labels.
              ------------------------------------------------------------ */}
          <Card title={t('admin.ai.topicTrendTitle')}>
            <div className="flex flex-col gap-4">
              <WarningBanner>{t('admin.ai.unverified')}</WarningBanner>
              <p className="text-xs text-ink-500">{t('admin.ai.topicTrendBasis')}</p>
              {board.topic_trend.length === 0 ? (
                <EmptyState
                  message={
                    board.provider.status === 'on'
                      ? t('admin.ai.topicTrendEmpty')
                      : t('admin.ai.disabled')
                  }
                />
              ) : (
                <BarChart
                  label={t('admin.ai.topicTrendTitle')}
                  emptyLabel={t('admin.ai.topicTrendEmpty')}
                  items={board.topic_trend.map<Slice>((row, index) => ({
                    key: row.code,
                    label: t(`admin.ai.topic.${row.code}`),
                    value: row.count,
                    color: SERIES_COLORS[index % SERIES_COLORS.length],
                  }))}
                />
              )}
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
}

/**
 * §14.4.5 rule 6, as a component so it cannot be forgotten on a new block.
 *
 * It is a plain sentence rather than a subtle caption: the claim it corrects —
 * that a deadline calculation is artificial intelligence — is the one a reader
 * arrives at this screen already primed to believe.
 */
function SqlBasisNote(): React.JSX.Element {
  const { t } = useI18n();
  return (
    <p className="rounded-chip bg-surface-50 px-3 py-2 text-sm text-ink-700">
      {t('admin.ai.sqlBasis')}
    </p>
  );
}

function RiskRow({ item }: { item: RiskItem }): React.JSX.Element {
  const { t } = useI18n();
  return (
    <Tr>
      <Td>
        <Link
          to={`/murojaats/${item.id}`}
          className="font-semibold tracking-ref text-brand-700 hover:underline"
        >
          {item.reference}
        </Link>
      </Td>
      <Td>{item.mahalla_name}</Td>
      <Td>{t(`shared.murojaat.status.${item.status}`)}</Td>
      <Td>
        <div className="flex flex-col gap-1">
          <span>{formatDate(item.due_at)}</span>
          <Badge tone={BUCKET_TONE[item.risk.bucket] ?? 'neutral'}>
            {t(`admin.ai.bucket.${item.risk.bucket}`)}
          </Badge>
        </div>
      </Td>
      <Td>
        <RiskBars risk={item.risk} />
      </Td>
    </Tr>
  );
}

/**
 * §14.4.5 rule 4 — THE SCORE IS NEVER A BARE NUMBER.
 *
 * "83" with no basis is a number a staff member will act on without knowing
 * what it means. So the four SQL components are drawn as their own labelled
 * segments against their own maxima, and the total is shown as `n/60` — the
 * denominator included precisely so nobody reads it as a percentage or as a
 * score out of 100.
 *
 * The AI severity is deliberately NOT here. It belongs to one appeal, it is
 * unverified, and §14.4.5 rule 3 keeps machine output out of every list row —
 * so a reader sees it only on the detail screen, next to its disclaimer.
 */
function RiskBars({ risk }: { risk: SqlRisk }): React.JSX.Element {
  const { t } = useI18n();
  const parts = [
    { key: 'deadline', value: risk.deadline, max: SQL_RISK_WEIGHTS.deadline },
    { key: 'repeat', value: risk.repeat, max: SQL_RISK_WEIGHTS.repeat },
    { key: 'statusAge', value: risk.status_age, max: SQL_RISK_WEIGHTS.statusAge },
    { key: 'backlog', value: risk.backlog, max: SQL_RISK_WEIGHTS.backlog },
  ] as const;

  return (
    <div className="flex min-w-52 flex-col gap-1">
      <span className="text-sm font-semibold tabular-nums text-ink-900">
        {t('admin.ai.riskSqlTotal', { total: risk.total, max: SQL_RISK_MAX })}
      </span>
      {parts.map((part, index) => (
        <span key={part.key} className="grid grid-cols-[8.5rem_1fr_2rem] items-center gap-2">
          <span className="text-xs text-ink-500">{t(`admin.ai.risk.${part.key}`)}</span>
          <span className="h-2 rounded-chip bg-surface-100">
            <span
              className={`block h-2 rounded-chip ${SERIES_COLORS[index % SERIES_COLORS.length]!.bg}`}
              style={{ width: `${String((part.value / part.max) * 100)}%` }}
            />
          </span>
          <span className="text-right text-xs tabular-nums text-ink-700">{part.value}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * §14.4.5 rule 5 — `off` is a SENTENCE, never an empty board.
 *
 * The counts are shown either way, and they are the honest diagnostic when the
 * two disagree: a rising `pending` beside `status: off` says the queue is
 * building and nothing is draining it, which is exactly what an operator needs
 * to see when a worker has refused to start.
 */
function ProviderCard({
  provider,
}: {
  provider: components['schemas']['AiProviderStatus'];
}): React.JSX.Element {
  const { t } = useI18n();
  const on = provider.status === 'on';

  return (
    <Card title={t('admin.ai.providerTitle')}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Badge tone={on ? 'success' : 'neutral'}>
            {on ? t('admin.ai.providerOn') : t('admin.ai.providerOff')}
          </Badge>
          {on && provider.model ? (
            <span className="text-xs text-ink-500">
              {t('admin.ai.model')} {provider.model}
            </span>
          ) : null}
        </div>

        {/* The honest disabled state, in full, not a dash. */}
        {!on ? <p className="text-sm text-ink-700">{t('admin.ai.disabled')}</p> : null}

        <dl className="flex flex-wrap gap-6 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-label text-ink-500">
              {t('admin.ai.analyzedCount')}
            </dt>
            <dd className="font-semibold tabular-nums text-ink-900">
              {provider.analyzed_count ?? 0}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-label text-ink-500">
              {t('admin.ai.pendingCount')}
            </dt>
            <dd className="font-semibold tabular-nums text-ink-900">
              {provider.pending_count ?? 0}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-label text-ink-500">
              {t('admin.ai.failedCount')}
            </dt>
            <dd className="font-semibold tabular-nums text-ink-900">
              {provider.failed_count ?? 0}
            </dd>
          </div>
        </dl>

        {/* The closed topic set, stated once on the screen that uses it. A
            reader who sees only `roads` and `water` in the trend should be able
            to tell "these are the only two raised" from "these are the only two
            the set can express". */}
        <p className="text-xs text-ink-400">
          {t('admin.ai.topicSetNote', { count: AI_TOPICS.length })}
        </p>
      </div>
    </Card>
  );
}
